import { ProductFormComponent } from './product-form.js';

console.log('[PhotoPrintForm] Module loaded');
console.log('[PhotoPrintForm] ProductFormComponent imported:', ProductFormComponent);

/**
 * Photo Print Form Component
 *
 * Extends ProductFormComponent to add S3 photo upload integration for print products.
 * Handles upload progress display and blocks cart submission until upload completes.
 */
class PhotoPrintFormComponent extends ProductFormComponent {
  #uploadInProgress = false;

  connectedCallback() {
    console.log('[PhotoPrintForm] Component connected');
    super.connectedCallback();

    // Listen for photo upload events to toggle button visibility
    this.#setupPhotoEventListeners();

    // Initial check - will be updated again by photo-upload:changed event if photo is restored
    this.#updateButtonVisibility();
  }

  /**
   * Setup event listeners for photo upload/removal
   */
  #setupPhotoEventListeners() {
    document.addEventListener('photo-upload:changed', () => {
      console.log('[PhotoPrintForm] Photo uploaded, updating button visibility');
      this.#updateButtonVisibility();
    });

    document.addEventListener('photo-upload:removed', () => {
      console.log('[PhotoPrintForm] Photo removed, updating button visibility');
      this.#updateButtonVisibility();
    });

    // Click handler for "Upload photo" button
    const uploadPhotoButton = this.querySelector('[data-upload-photo-button]');
    console.log('[PhotoPrintForm] Looking for upload photo button:', uploadPhotoButton);
    console.log('[PhotoPrintForm] Component innerHTML:', this.innerHTML.substring(0, 500));

    if (uploadPhotoButton) {
      console.log('[PhotoPrintForm] Adding click listener to upload photo button');
      uploadPhotoButton.addEventListener('click', () => {
        console.log('[PhotoPrintForm] Upload photo button clicked, opening modal');
        document.dispatchEvent(new CustomEvent('photo-upload:open-modal'));
      });
    } else {
      console.error('[PhotoPrintForm] Upload photo button not found!');
    }
  }

  /**
   * Update button state based on photo upload state
   * With the new inline photo upload, we always show the add-to-cart button
   * but disable it until a photo is uploaded
   */
  #updateButtonVisibility() {
    const photoUpload = document.querySelector('photo-upload-component');
    // Check for either uploadedFile (new upload) or uploadedImageURL (restored from localStorage)
    const hasPhoto = photoUpload && (photoUpload.uploadedFile || photoUpload.uploadedImageURL || photoUpload.croppedImageURL);

    const addToCartButton = this.querySelector('[data-add-to-cart-button]');
    const uploadPhotoButton = this.querySelector('[data-upload-photo-button]');

    console.log('[PhotoPrintForm] Updating visibility - hasPhoto:', hasPhoto);

    // Always show the add-to-cart button, hide the old upload photo button
    if (addToCartButton) {
      addToCartButton.style.display = '';
    }
    if (uploadPhotoButton) {
      uploadPhotoButton.style.display = 'none';
    }

    // Enable/disable based on photo state
    const submitButton = this.refs.addToCartButtonContainer?.refs.addToCartButton;
    if (submitButton) {
      if (hasPhoto) {
        submitButton.disabled = false;
        submitButton.removeAttribute('disabled');
      } else {
        submitButton.disabled = true;
        submitButton.setAttribute('disabled', 'disabled');
      }
    }
  }

  /**
   * Override parent's handleSubmit to intercept and handle photo upload first
   */
  handleSubmit = async (event) => {
    console.log('[PhotoPrintForm] Form submit intercepted');
    console.log('[PhotoPrintForm] Event:', event);
    console.log('[PhotoPrintForm] Event type:', event.type);
    console.log('[PhotoPrintForm] Event target:', event.target);
    const { addToCartTextError } = this.refs;

    // If we're already uploading, prevent duplicate submission
    if (this.#uploadInProgress) {
      console.log('[PhotoPrintForm] Already uploading, preventing submission');
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    // Check if photo upload component exists
    const photoUpload = document.querySelector('photo-upload-component');
    console.log('[PhotoPrintForm] Photo upload component:', photoUpload);
    console.log('[PhotoPrintForm] Has uploaded file:', photoUpload?.uploadedFile);
    console.log('[PhotoPrintForm] Has uploadedImageURL:', photoUpload?.uploadedImageURL);
    console.log('[PhotoPrintForm] Has croppedImageURL:', photoUpload?.croppedImageURL);

    // If no photo upload component or no photo uploaded, prevent submission
    const hasPhoto = photoUpload && (photoUpload.uploadedFile || photoUpload.uploadedImageURL || photoUpload.croppedImageURL);
    if (!hasPhoto) {
      event.preventDefault();
      event.stopImmediatePropagation();

      // Show error message
      if (addToCartTextError) {
        addToCartTextError.classList.remove('hidden');
        const textNode = addToCartTextError.childNodes[2];
        const errorMessage = 'Please upload a photo before adding to cart.';
        if (textNode) {
          textNode.textContent = errorMessage;
        } else {
          addToCartTextError.appendChild(document.createTextNode(errorMessage));
        }

        // Auto-hide error after 5 seconds
        setTimeout(() => {
          addToCartTextError.classList.add('hidden');
        }, 5000);
      }
      return;
    }

    // Photo exists, prevent default submission and do upload first
    console.log('[PhotoPrintForm] Preventing default and stopping propagation');
    event.preventDefault();
    event.stopImmediatePropagation();
    console.log('[PhotoPrintForm] Default prevented:', event.defaultPrevented);

    this.#uploadInProgress = true;
    console.log('[PhotoPrintForm] Starting photo upload...');

    try {
      // Show uploading state
      await this.#handlePhotoUpload(photoUpload);

      // Photo uploaded successfully
      // Now let the parent handleSubmit do its thing
      // We create a new event and let it bubble normally
      this.#uploadInProgress = false;

      console.log('[PhotoPrintForm] Photo uploaded successfully, calling parent handleSubmit');
      // Trigger parent's handleSubmit directly
      super.handleSubmit(event);

    } catch (error) {
      console.error('[PhotoPrintForm] Photo upload failed:', error);
      this.#uploadInProgress = false;

      // Show error message
      if (addToCartTextError) {
        addToCartTextError.classList.remove('hidden');
        const textNode = addToCartTextError.childNodes[2];
        const errorMessage = 'Failed to upload photo. Please try again.';
        if (textNode) {
          textNode.textContent = errorMessage;
        } else {
          addToCartTextError.appendChild(document.createTextNode(errorMessage));
        }

        // Auto-hide error after 5 seconds
        setTimeout(() => {
          addToCartTextError.classList.add('hidden');
        }, 5000);
      }

      // Hide uploading state
      this.#hideUploadingState();
    }
  }

  /**
   * Handles photo upload to S3 before cart submission
   * @param {PhotoUploadComponent} photoUpload - The photo upload component
   */
  async #handlePhotoUpload(photoUpload) {
    // Show uploading state
    this.#showUploadingState();

    // Listen for progress updates
    const progressHandler = (event) => {
      if (event.detail.status === 'progress') {
        this.#updateUploadProgress(event.detail.percent);
      }
    };
    photoUpload.addEventListener('photo-upload:s3-status', progressHandler);

    try {
      // Upload to S3
      const s3Key = await photoUpload.uploadToS3();

      if (!s3Key) {
        throw new Error('S3 upload returned no key');
      }

      // Update form property with S3 key
      photoUpload.updateFormPropertyWithS3Key(s3Key);
    } finally {
      // Clean up event listener
      photoUpload.removeEventListener('photo-upload:s3-status', progressHandler);

      // Hide uploading state
      this.#hideUploadingState();
    }
  }

  /**
   * Shows the upload progress modal overlay
   */
  #showUploadingState() {
    // Create modal if it doesn't exist
    if (!this.uploadModal) {
      this.#createUploadModal();
    }

    // Show the modal
    this.uploadModal.style.display = 'flex';
    this.#updateUploadProgress(0);

    // Disable the button to prevent double submission
    if (this.refs.addToCartButtonContainer) {
      this.refs.addToCartButtonContainer.disable();
    }
  }

  /**
   * Creates the upload progress modal
   */
  #createUploadModal() {
    this.uploadModal = document.createElement('div');
    this.uploadModal.className = 'photo-upload-modal-overlay';
    this.uploadModal.innerHTML = `
      <div class="photo-upload-modal">
        <div class="photo-upload-modal__spinner"></div>
        <div class="photo-upload-modal__text">Uploading your photo</div>
        <div class="photo-upload-modal__progress-bar">
          <div class="photo-upload-modal__progress-fill" data-progress-fill></div>
        </div>
        <div class="photo-upload-modal__percent"><span data-progress-percent>0</span>%</div>
      </div>
    `;

    // Add styles
    const styles = document.createElement('style');
    styles.textContent = `
      .photo-upload-modal-overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.6);
        display: none;
        align-items: center;
        justify-content: center;
        z-index: 9999;
        backdrop-filter: blur(2px);
      }
      .photo-upload-modal {
        background: white;
        border-radius: 16px;
        padding: 32px 48px;
        text-align: center;
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
        min-width: 280px;
      }
      .photo-upload-modal__spinner {
        width: 48px;
        height: 48px;
        border: 3px solid #e5e5e5;
        border-top-color: #1a1a1a;
        border-radius: 50%;
        margin: 0 auto 20px;
        animation: upload-modal-spin 0.8s linear infinite;
      }
      @keyframes upload-modal-spin {
        to { transform: rotate(360deg); }
      }
      .photo-upload-modal__text {
        font-size: 18px;
        font-weight: 600;
        color: #1a1a1a;
        margin-bottom: 20px;
      }
      .photo-upload-modal__progress-bar {
        height: 8px;
        background: #e5e5e5;
        border-radius: 4px;
        overflow: hidden;
        margin-bottom: 12px;
      }
      .photo-upload-modal__progress-fill {
        height: 100%;
        background: #1a1a1a;
        border-radius: 4px;
        width: 0%;
        transition: width 0.2s ease;
      }
      .photo-upload-modal__percent {
        font-size: 14px;
        color: #666;
      }
    `;

    document.head.appendChild(styles);
    document.body.appendChild(this.uploadModal);
  }

  /**
   * Hides the upload progress modal
   */
  #hideUploadingState() {
    if (this.uploadModal) {
      this.uploadModal.style.display = 'none';
    }

    // Re-enable the button
    if (this.refs.addToCartButtonContainer) {
      this.refs.addToCartButtonContainer.enable();
    }
  }

  /**
   * Updates the upload progress percentage
   * @param {number} percent - Progress percentage (0-100)
   */
  #updateUploadProgress(percent) {
    if (!this.uploadModal) return;

    const progressFill = this.uploadModal.querySelector('[data-progress-fill]');
    const progressPercent = this.uploadModal.querySelector('[data-progress-percent]');

    if (progressFill) {
      progressFill.style.width = `${percent}%`;
    }

    if (progressPercent) {
      progressPercent.textContent = Math.round(percent);
    }
  }
}

// Register custom element
if (!customElements.get('photo-print-form-component')) {
  console.log('[PhotoPrintForm] Registering custom element');
  customElements.define('photo-print-form-component', PhotoPrintFormComponent);
  console.log('[PhotoPrintForm] Custom element registered successfully');
} else {
  console.log('[PhotoPrintForm] Custom element already registered');
}

// The custom element is registered, but if the HTML element appears in the DOM AFTER
// this script runs (due to async section rendering), connectedCallback won't be called.
// The browser automatically calls connectedCallback when a custom element is added to the DOM,
// so we don't actually need to do anything special here - it should just work!

console.log('[PhotoPrintForm] Module setup complete - waiting for elements to be added to DOM');

export default PhotoPrintFormComponent;
